import { 
  LibraryItem, 
  LibraryItemLocationData, 
  QuotationBlock, 
  RestaurantInfo, 
  AccommodationInfo,
  TourInfo,
  TransportationInfo,
  FlightInfo
} from "@/components/quotation/types";
import { LibraryService } from './library-service';

/**
 * Extrair título do item com base no tipo
 */
export function getItemTitle(item: any, type: string, index?: number): string {
  switch (type) {
    case 'block':
      return item.title || 'Bloco';
    case 'restaurant':
      return item.name || (index !== undefined ? `Restaurante #${index + 1}` : 'Restaurante');
    case 'accommodation':
      return item.name || (index !== undefined ? `Hospedagem #${index + 1}` : 'Hospedagem');
    case 'tour':
      return item.name || (index !== undefined ? `Passeio #${index + 1}` : 'Tour');
    case 'transportation':
      return item.name || (index !== undefined ? `Traslado #${index + 1}` : 'Transporte');
    case 'flight':
      return `Voo ${item.airline || ''} ${item.flightNumber || ''}`.trim() || 'Voo';
    default:
      return 'Item';
  }
}

/**
 * Extrair localização do item com base no tipo
 */
export function getItemLocation(item: any, type: string): string {
  switch (type) {
    case 'block':
    case 'restaurant':
    case 'accommodation':
    case 'tour':
    case 'transportation':
      return item.location || '';
    case 'flight':
      return item.departureAirport && item.arrivalAirport 
        ? `${item.departureAirport} - ${item.arrivalAirport}`
        : '';
    default:
      return '';
  }
}

/**
 * Extrair dados de localização estruturados
 */
export function extractLocationData(item: any, type: string): LibraryItemLocationData {
  const locationData: LibraryItemLocationData = {};
  
  // Handle transportation items specially due to their departure/arrival structure
  if (type === 'transportation') {
    // For transportation, prioritize departure location data
    
    // Use departure address or location for formatted address
    if (item.departureAddress) {
      locationData.formattedAddress = item.departureAddress;
    } else if (item.departureLocation) {
      locationData.formattedAddress = item.departureLocation;
    } else {
      locationData.formattedAddress = getItemLocation(item, type);
    }
    
    // Use departure placeId
    if (item.departurePlaceId) {
      locationData.placeId = item.departurePlaceId;
    } else if (item.placeId) {
      locationData.placeId = item.placeId;
    }
    
    // Use departure coordinates
    if (item.departureCoordinates) {
      locationData.coordinates = { ...item.departureCoordinates };
    } else if (item.coordinates) {
      locationData.coordinates = { ...item.coordinates };
    }
    
    // Use departure city and country
    if (item.departureCity) {
      locationData.city = item.departureCity;
    } else if (item.city) {
      locationData.city = item.city;
    }
    
    if (item.departureCountry) {
      locationData.country = item.departureCountry;
    } else if (item.country) {
      locationData.country = item.country;
    }
    
    // Fallback to normal extraction if still needed
    if (!locationData.city || !locationData.country) {
      const formattedAddress = locationData.formattedAddress || getItemLocation(item, type);
      if (formattedAddress) {
        const parts = formattedAddress.split(',').map(p => p.trim());
        
        if (parts.length >= 2) {
          // O último segmento geralmente é o país
          if (!locationData.country) {
            locationData.country = parts[parts.length - 1];
          }
          
          // A cidade geralmente é o primeiro segmento após o nome do local
          if (!locationData.city) {
            if (parts.length > 2) {
              locationData.city = parts[1];
            } else {
              locationData.city = parts[0];
            }
          }
        }
      }
    }
    
    return locationData;
  }
  
  // For non-transportation items, use the standard extraction logic
  
  // Copiar endereço formatado
  const formattedAddress = getItemLocation(item, type);
  if (formattedAddress) {
    locationData.formattedAddress = formattedAddress;
  }
  
  // Copiar placeId se existir
  if (item.placeId) {
    locationData.placeId = item.placeId;
  }
  
  // Copiar coordenadas se existirem
  if (item.coordinates) {
    locationData.coordinates = { ...item.coordinates };
  } else if (item.latitude !== undefined && item.longitude !== undefined) {
    locationData.coordinates = {
      lat: item.latitude,
      lng: item.longitude
    };
  }

  // Usar diretamente os campos de cidade e país se existirem
  if (item.city) {
    locationData.city = item.city;
  }
  if (item.country) {
    locationData.country = item.country;
  }
  
  // Se não tiver cidade ou país, tentar extrair do endereço formatado
  if (formattedAddress && (!locationData.city || !locationData.country)) {
    const parts = formattedAddress.split(',').map(p => p.trim());
    
    if (parts.length >= 2) {
      // O último segmento geralmente é o país
      if (!locationData.country) {
        locationData.country = parts[parts.length - 1];
      }
      
      // A cidade geralmente é o primeiro segmento após o nome do local
      // Se tivermos mais de 2 partes, a cidade provavelmente está na segunda posição
      if (!locationData.city) {
        if (parts.length > 2) {
          locationData.city = parts[1];
        } else {
          locationData.city = parts[0];
        }
      }
    }
  }
  
  return locationData;
}

/**
 * Gerar novos IDs para um item e todos seus sub-itens
 */
export function generateNewIdsForQuotationItem(item: any, type: string): any {
  // Clone profundo do item
  const newItem = JSON.parse(JSON.stringify(item));
  
  // Gerar um novo ID para o item principal
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  
  // Atribuir novo ID conforme o tipo
  switch (type) {
    case 'block':
      newItem.id = `block_${timestamp}_${randomStr}`;
      break;
    case 'restaurant':
      newItem.id = `restaurant_${timestamp}_${randomStr}`;
      break;
    case 'accommodation':
      newItem.id = `accommodation_${timestamp}_${randomStr}`;
      break;
    case 'tour':
      newItem.id = `tour_${timestamp}_${randomStr}`;
      break;
    case 'transportation':
      newItem.id = `transportation_${timestamp}_${randomStr}`;
      break;
    case 'flight':
      newItem.id = `flight_${timestamp}_${randomStr}`;
      // Também gerar novos IDs para conexões de voo
      if (newItem.connections && Array.isArray(newItem.connections)) {
        newItem.connections = newItem.connections.map((conn: any) => ({
          ...conn,
          id: `connection_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
        }));
      }
      break;
    default:
      newItem.id = `item_${timestamp}_${randomStr}`;
  }
  
  // Adicionar metadado para rastrear a origem
  newItem._librarySource = {
    originId: item.id,
    addedAt: new Date(),
    isLinked: false
  };
  
  return newItem;
}

/**
 * Converter item da biblioteca para item de cotação
 */
export function libraryItemToQuotationItem(libraryItem: LibraryItem): any {
  // Clone dos dados do item
  const itemData = JSON.parse(JSON.stringify(libraryItem.data));
  
  // Gerar novos IDs para o item
  return generateNewIdsForQuotationItem(itemData, libraryItem.type);
}

/**
 * Convert an item to a library item
 */
export function itemToLibraryItem(
  item: any, 
  type: string, 
  metadata: { 
    title?: string; 
    description?: string; 
    tags?: string[]; 
    city?: string; 
    country?: string;
    generateId?: boolean; // Optional flag to control ID generation
  }
): LibraryItem {
  // Clone profundo do item
  const itemData = JSON.parse(JSON.stringify(item));
  
  // Add source tracking to data
  itemData._librarySource = {
    originId: item.id, // Store the original item ID for reference
    originType: type
  };
  
  // Extrair dados de localização
  const locationData = extractLocationData(item, type);
  
  // Sobrescrever com dados fornecidos
  if (metadata.city) locationData.city = metadata.city;
  if (metadata.country) locationData.country = metadata.country;
  
  // Identificar uma imagem para thumbnail
  let thumbnailUrl: string | undefined;
  if (itemData.images && itemData.images.length > 0) {
    thumbnailUrl = itemData.images[0];
  }
  
  // Generate an ID only if explicitly requested (for updates)
  // For new items, Supabase will generate its own ID
  const id = metadata.generateId 
    ? `lib_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
    : undefined;
  
  // Criar o item de biblioteca
  const libraryItem: Partial<LibraryItem> = {
    type: type as any,
    data: itemData,
    title: metadata.title || getItemTitle(item, type),
    description: metadata.description || '',
    tags: metadata.tags || [],
    location: locationData,
    thumbnailUrl,
    updatedAt: new Date()
  };
  
  // Only add ID if it was generated
  if (id) {
    libraryItem.id = id;
    libraryItem.createdAt = new Date();
  }
  
  return libraryItem as LibraryItem;
}

/**
 * Verificar se um item já está salvo na biblioteca
 * Compara o ID original, conteúdo ou uma combinação deles para determinar se o item existe
 */
export async function isItemSavedInLibrary(
  item: any, 
  type: 'block' | 'restaurant' | 'accommodation' | 'tour' | 'transportation' | 'flight'
): Promise<boolean> {
  if (!item || !item.id) return false;
  
  try {
    // Obter todos os itens da biblioteca do mesmo tipo
    const filterOptions = { type };
    const libraryItems = await LibraryService.getItems(filterOptions);
    
    // Verificar se não temos items (proteção contra erros)
    if (!libraryItems || !Array.isArray(libraryItems)) {
      console.error('Error: libraryItems is not an array', { libraryItems });
      return false;
    }
    
    // Verificar se algum item na biblioteca corresponde ao item atual
    return libraryItems.some(libraryItem => {
      // Verificar se o ID do item é o mesmo ou se foi derivado do item original
      const isIdMatch = libraryItem.data?.id === item.id;
      const isOriginMatch = libraryItem.data?._librarySource?.originId === item.id;
      
      // Para blocos, também podemos comparar título e localização para maior precisão
      if (type === 'block') {
        const isTitleMatch = libraryItem.title === (item.title || getItemTitle(item, type));
        const isLocationMatch = libraryItem.location?.formattedAddress === getItemLocation(item, type);
        
        // Considera uma correspondência se o ID corresponder OU se título e localização corresponderem
        return isIdMatch || isOriginMatch || (isTitleMatch && isLocationMatch);
      }
      
      // Para outros tipos, a correspondência de ID ou origem é suficiente
      return isIdMatch || isOriginMatch;
    });
  } catch (error) {
    console.error('Erro ao verificar se o item está na biblioteca:', error);
    return false;
  }
} 